import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { BaseFormContext } from '~/machines/base-crud';
import { CMETraining } from '../../../types';
import { optionSchema } from '../../types';
import { Events } from './events';

const strictDateSchema = z.iso.date(ERROR_MESSAGES.INVALID_DATE);

export const cmeTrainingSchema = z
  .object({
    activityDate: z
      .string()
      .refine((date) => strictDateSchema.safeParse(date).success, { message: 'Invalid date' })
      .refine((date) => new Date(date) < new Date(), { message: 'Date must be in the past' }),
    courseTitle: z
      .string()
      .trim()
      .min(1, { message: ERROR_MESSAGES.REQUIRED })
      .max(128, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(128) }),
    creditType: optionSchema,
    eduProviderName: z.string().max(64, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64) }),
    creditsEarned: z.union([
      z.string(),
      z.number().min(0, { message: ERROR_MESSAGES.NON_NEGATIVE_INT }),
    ]),
    certReceived: z
      .string()
      .trim()
      .max(64, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64) }),
    cost: z.number().max(99999999, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(8) }),
    costFormatted: z.string(),
    location: z.string().max(64, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(64) }),
    details: z.string().max(4096, { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096) }),
    id: z.number().optional(),
    validFlag: z.boolean().optional(),
    warnings: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (schema) => {
      return schema.cost.toString().length <= 8;
    },
    { message: ERROR_MESSAGES.MAX_CHARACTER_LIMIT(8), path: ['costFormatted'] }
  );

export type CMETrainingFormValues = z.infer<typeof cmeTrainingSchema>;
export type CreateCMETrainingFormValues = Omit<CMETrainingFormValues, 'id'>;

export type CMESortOptions =
  | 'activity_date'
  | 'course_title'
  | 'credit_type'
  | 'credits_earned'
  | 'id';

export type CMESort = {
  sortBy: CMESortOptions;
  activity_date: number;
  course_title: number;
  credit_type: number;
  credits_earned: number;
  id: number;
  page: number;
  pageSize: number;
};

export type Context = BaseFormContext<CMETrainingFormValues, CMESort>;
export interface CMETrainingResponse {
  data: CMETraining;
}

export interface CMETrainingsPaginatedResponse {
  data: {
    data: CMETraining[];
    page: number;
    pageSize: number;
  };
}
export interface CMETrainingsResponse {
  data: CMETraining[];
}

export interface Scope {
  context: Context;
  event: Events;
}

export type sortOptions =
  | 'activity_date'
  | 'course_title'
  | 'credit_type'
  | 'credits_earned'
  | 'id';
